import React, { FC } from "react";
import * as styles from "./Footer.module.scss";
import {
  SectionContainer,
  Button,
  BlockContentValue
} from "@silversea-ssc/ui-components-react";
import FollowUs from "components/Footer/components/FollowUs/FollowUs";
import ContactUs from "components/Footer/components/ContactUs/ContactUs";
import FooterNav from "./components/FooterNav/FooterNav";
import SubFooter from "./components/SubFooter/SubFooter";

import { INavLink, INavLinks } from "components/HeaderNavBar/type";
import {
  FooterMenuGroupsQuery,
  FooterMenuItemsFragment,
  FormDisclaimerFragment
} from "types/graphql-types";
import { isPresent } from "src/utils/checks";
import type { FooterUrls } from "src/urls/footer_urls";
import { useLingui } from "@lingui/react/macro";

const FooterCDCDisclaimer = React.lazy(
  () => import("./components/FooterCDCDisclaimer/FooterCDCDisclaimer")
);
const Subscribe = React.lazy(() => import("./components/Subscribe/Subscribe"));
const BrandLogosFooter = React.lazy(
  () => import("./components/BrandLogosFooter/BrandLogosFooter")
);

interface FooterProps {
  searchPageUrl?: string;
  raqUrl?: string;
  rabUrl?: string;
  urls: FooterUrls;
  footerMenuGroups: NonNullable<
    NonNullable<FooterMenuGroupsQuery["site"]>["footerMenuGroups"]
  >;
  subscribeFormTitle: string | undefined;
  subscribeFormContent: BlockContentValue | undefined;
  formDisclaimerContent: FormDisclaimerFragment | undefined;
  subscribePrivacyPolicyUrl: INavLink;
  offerPageUrl: string | undefined;
}

const FooterBase: FC<React.PropsWithChildren<FooterProps>> = ({
  searchPageUrl,
  raqUrl,
  rabUrl,
  urls,
  footerMenuGroups,
  subscribeFormTitle,
  subscribeFormContent,
  formDisclaimerContent,
  subscribePrivacyPolicyUrl,
  offerPageUrl
}) => {
  const { t } = useLingui();
  const { subFooterLinksLinks } = urls;

  return (
    <React.Fragment>
      <footer className={styles.footer} id="footer" data-content-name="footer">
        <SectionContainer backgroundColor="transparent">
          <div className={styles.mainFooter}>
            <div className={styles.footerLeft}>
              <FollowUs className={styles.followUs} />
              <div className={styles.footerBoxes}>
                <div className={styles.contactUs}>
                  <ContactUs />
                </div>
                <div className={styles.subscribe} suppressHydrationWarning>
                  <React.Suspense>
                    <Subscribe
                      formTitle={subscribeFormTitle}
                      formContent={subscribeFormContent}
                      formDisclaimerContent={formDisclaimerContent}
                      privacyPolicyUrl={subscribePrivacyPolicyUrl}
                      offerPageUrl={offerPageUrl}
                    />
                  </React.Suspense>
                </div>
              </div>
              <div className={styles.ctaContainer}>
                <Button
                  variant="primary"
                  href={raqUrl}
                  full={true}
                  inverted
                  dataContentPiece="request_a_quote"
                >
                  {t`Request a Quote`}
                </Button>
                <Button
                  variant="primary"
                  href={rabUrl}
                  full={true}
                  inverted
                  dataContentPiece="view_the_brochures"
                >
                  {t`View the Brochures`}
                </Button>
              </div>
            </div>
            <div className={styles.footerNavs}>
              {footerMenuGroups.map((group) => {
                const target = group?.target;
                if (!isPresent(target)) return null;

                const targetTitle = target.title?.localized;
                const menuItems = group?.menuItems?.filter(isPresent);
                return (
                  <FooterNav
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    id={target._id!}
                    key={target._id}
                    label={targetTitle || ""}
                    links={generateGroupMenuItems(menuItems)}
                  >
                    <></>
                  </FooterNav>
                );
              })}
            </div>
          </div>
          <div className={styles.subFooter}>
            <SubFooter links={subFooterLinksLinks} searchUrl={searchPageUrl} />
          </div>
          <React.Suspense>
            <FooterCDCDisclaimer />
          </React.Suspense>
        </SectionContainer>
        <React.Suspense>
          <BrandLogosFooter />
        </React.Suspense>
      </footer>
    </React.Fragment>
  );
};

const generateGroupMenuItems = (
  group: FooterMenuItemsFragment[] | undefined
): INavLinks => {
  if (!group) return [];

  return group.map(menuItemToNavLink).filter(isPresent);
};

const menuItemToNavLink = (
  item: FooterMenuItemsFragment
): INavLink | undefined => {
  if ("target" in item) {
    return {
      title: item.target?.title?.localized || "",
      href: item.target?.fullPath || "",
      filters: item.filters
    };
  }

  if ("label" in item) {
    return {
      href: item.url?.localized || "",
      title: item.label?.localized || "",
      filters: undefined
    };
  }

  return undefined;
};

export const Footer = React.memo(FooterBase);
